import { QuestionState, TypeArray } from "../types"
import { reducerTypeGuard } from "../reducers/reducerGuards"
import { actionCreatorTypeGuard } from "../reducers/actionCreators"
import {
  processActionMeta,
  getArrayTypeReducerGuard
} from "../reducers"
import { QuestionStateSchema } from "../schemas"

import _ from "lodash"

export type BlockState = {
  questionStates: Array<QuestionState | null>
  completed: boolean
}

const reducerAction = {
  questionStates: actionCreatorTypeGuard<
    BlockState,
    Array<QuestionState | null>
  >("questionStates", "BlockState"),
  completed: actionCreatorTypeGuard<BlockState, boolean>(
    "completed",
    "BlockState"
  ),

  __record: actionCreatorTypeGuard<TypeArray, BlockState>(
    "BlockState",
    "__record"
  ),
  __create: actionCreatorTypeGuard<TypeArray, null | Partial<BlockState>>(
    "create",
    "__create/BlockState"
  ),
  __delete: actionCreatorTypeGuard<TypeArray, number>("delete", "__record")
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof BlockState
    | "__record"
    | "__delete"
    | "__create"]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const blockStateAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

const validationOptions = {
  strict: true,
  abortEarly: false,
  stripUnknown: true,
  recursive: true
}

export const blockStateReducer = reducerTypeGuard<BlockState>()
  .case(...reducerAction.questionStates)
  .case(...reducerAction.completed)
  .default((state, action: any) => {
    let prevState = _.cloneDeep(state)
    action = processActionMeta(action)

    const {
      prop,
      idx,
      nodes
    }: { prop: keyof BlockState; idx: number; nodes: Array<any> } = action?.meta
    if (["questionStates"].includes(prop)) {
      if (!prevState?.[prop]) {
        prevState = { ...prevState, [prop]: [] }
      }

      if (prop == "questionStates" && !prevState[prop]?.[idx]) {
        prevState[prop][idx] = QuestionStateSchema.cast(
          { interactionType: prevState[prop][0]?.interactionType },
          validationOptions
        )
      }
    }

    switch (action?.meta?.prop) {
      case "questionStates":
        return getArrayTypeReducerGuard<BlockState, QuestionState | null>(
          "BlockState",
          "questionStates",
          "QuestionState",
          prevState,
          action
        )
    }

    return prevState
  })
